import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '../../notifications';
import {
  getConversationAiSettings,
  updateConversationAiSettings,
} from '../services/conversationService';
import { ConversationAiSettings, ConversationAiToolOption } from '../types/Conversation';
import detailsStyles from './ConversationDetails.module.css';

interface ConversationAiSettingsPanelProps {
  conversationId: string;
  canManage: boolean;
  expanded: boolean;
  onClose: () => void;
}

const MAX_INSTRUCTION_LENGTH = 4000;

const ConversationAiSettingsPanel: React.FC<ConversationAiSettingsPanelProps> = ({
  conversationId,
  canManage,
  expanded,
  onClose,
}) => {
  const { t } = useTranslation('conversations');
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [inheritTools, setInheritTools] = useState(true);
  const [tools, setTools] = useState<ConversationAiToolOption[]>([]);
  const [savedInstruction, setSavedInstruction] = useState('');
  const [savedInheritTools, setSavedInheritTools] = useState(true);
  const [savedEnabledIds, setSavedEnabledIds] = useState<string[]>([]);
  const [toolQuery, setToolQuery] = useState('');

  const applySettings = useCallback((settings: ConversationAiSettings) => {
    const nextInstruction = settings.instructionOverride ?? '';
    const enabledIds = settings.availableTools.filter(tool => tool.enabled).map(tool => tool.functionId);
    setInstruction(nextInstruction);
    setInheritTools(settings.inheritTools);
    setTools(settings.availableTools);
    setSavedInstruction(nextInstruction);
    setSavedInheritTools(settings.inheritTools);
    setSavedEnabledIds(enabledIds);
    setToolQuery('');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getConversationAiSettings(conversationId);
      applySettings(settings);
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : t('failedToLoadAiAssistantSettings', { defaultValue: 'Failed to load AI assistant settings.' }),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [applySettings, conversationId, showToast, t]);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    void load();
  }, [expanded, load]);

  const enabledCount = tools.filter(tool => tool.enabled).length;
  const enabledIds = tools.filter(tool => tool.enabled).map(tool => tool.functionId);
  const dirty = instruction.trim() !== savedInstruction.trim()
    || inheritTools !== savedInheritTools
    || (!inheritTools && serializedIds(enabledIds) !== serializedIds(savedEnabledIds));

  const filteredTools = useMemo(() => {
    const query = toolQuery.trim().toLowerCase();
    if (!query) {
      return tools;
    }
    return tools.filter(tool => tool.displayName.toLowerCase().includes(query));
  }, [toolQuery, tools]);

  const toggleTool = (functionId: string) => {
    if (!canManage || saving) {
      return;
    }
    if (inheritTools) {
      setInheritTools(false);
    }
    setTools(current => current.map(tool => (
      tool.functionId === functionId ? { ...tool, enabled: !tool.enabled } : tool
    )));
  };

  const setAllFiltered = (enabled: boolean) => {
    if (!canManage || saving) {
      return;
    }
    if (inheritTools) {
      setInheritTools(false);
    }
    const visible = new Set(filteredTools.map(tool => tool.functionId));
    setTools(current => current.map(tool => (
      visible.has(tool.functionId) ? { ...tool, enabled } : tool
    )));
  };

  const save = async () => {
    if (!canManage || saving) {
      return;
    }

    setSaving(true);
    try {
      const settings = await updateConversationAiSettings(conversationId, {
        instructionOverride: instruction,
        inheritTools,
        enabledFunctionIds: tools.filter(tool => tool.enabled).map(tool => tool.functionId),
      });
      applySettings(settings);
      showToast(t('aiAssistantSettingsSaved', { defaultValue: 'AI assistant settings saved.' }), 'success');
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : t('failedToSaveAiAssistantSettings', { defaultValue: 'Failed to save AI assistant settings.' }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!expanded) {
    return null;
  }

  return (
    <section className={detailsStyles.aiSettingsPanel} aria-label={t('aiAssistantSettings', { defaultValue: 'AI assistant settings' })}>
      <header className={detailsStyles.aiSettingsHeader}>
        <div className={detailsStyles.aiSettingsHeading}>
          <span className={detailsStyles.aiSettingsMark} aria-hidden="true">
            {renderSparkleIcon()}
          </span>
          <div>
            <div className={detailsStyles.aiSettingsTitle}>{t('aiAssistantSettings', { defaultValue: 'AI for this chat' })}</div>
            <p className={detailsStyles.aiSettingsHelp}>
              {t('aiAssistantSettingsHelp', { defaultValue: 'Give this conversation its own voice and tools.' })}
            </p>
          </div>
        </div>
        <button
          type="button"
          className={detailsStyles.aiSettingsClose}
          onClick={onClose}
          aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
        >
          {renderCloseIcon()}
        </button>
      </header>

      {loading ? (
        <div className={detailsStyles.aiSettingsBody}>
          <div className={detailsStyles.aiSettingsSkeleton} />
          <div className={detailsStyles.aiSettingsSkeleton} />
        </div>
      ) : (
        <>
          <div className={detailsStyles.aiSettingsBody}>
            <div className={detailsStyles.aiSettingsCard}>
              <div className={detailsStyles.aiSettingsCardLabel}>
                {t('aiInstructionOverride', { defaultValue: 'Extra instructions' })}
              </div>
              <p className={detailsStyles.aiSettingsCardHint}>
                {t('aiInstructionOverrideHint', { defaultValue: 'The assistant reads these first, before company prompts.' })}
              </p>
              <textarea
                className={detailsStyles.aiSettingsTextarea}
                value={instruction}
                maxLength={MAX_INSTRUCTION_LENGTH}
                disabled={!canManage || saving}
                onChange={event => setInstruction(event.target.value)}
                placeholder={t('aiInstructionOverridePlaceholder', { defaultValue: 'e.g. Only answer in Azerbaijani. Never share prices. Tag an admin for refunds.' })}
              />
              <div className={detailsStyles.aiSettingsCounter}>
                {instruction.trim().length}/{MAX_INSTRUCTION_LENGTH}
              </div>
            </div>

            <div className={detailsStyles.aiSettingsCard}>
              <div className={detailsStyles.aiSettingsCardLabel}>
                {t('conversationAiTools', { defaultValue: 'Tools' })}
              </div>
              <div className={detailsStyles.aiSettingsMode} role="tablist" aria-label={t('conversationAiTools', { defaultValue: 'Tools' })}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={inheritTools}
                  className={`${detailsStyles.aiSettingsModeBtn} ${inheritTools ? detailsStyles.aiSettingsModeBtnActive : ''}`}
                  disabled={!canManage || saving}
                  onClick={() => setInheritTools(true)}
                >
                  {t('aiToolsCompanyDefaults', { defaultValue: 'Company defaults' })}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!inheritTools}
                  className={`${detailsStyles.aiSettingsModeBtn} ${!inheritTools ? detailsStyles.aiSettingsModeBtnActive : ''}`}
                  disabled={!canManage || saving}
                  onClick={() => setInheritTools(false)}
                >
                  {t('aiToolsCustom', { defaultValue: 'This conversation' })}
                </button>
              </div>

              {inheritTools ? (
                <div className={detailsStyles.aiSettingsInheritNote}>
                  <strong>{t('aiToolsInheritTitle', { defaultValue: 'Using company tools' })}</strong>
                  <span>
                    {t('aiToolsInheritSummary', {
                      defaultValue: 'This chat follows the company tool list. Switch to This conversation to pick a different set.',
                      count: tools.length,
                      enabled: enabledCount,
                      total: tools.length,
                    })}
                  </span>
                </div>
              ) : tools.length === 0 ? (
                <div className={detailsStyles.aiSettingsEmpty}>
                  {t('noAiToolsAvailable', { defaultValue: 'No tools are available for this conversation type.' })}
                </div>
              ) : (
                <>
                  <div className={detailsStyles.aiSettingsToolBar}>
                    <input
                      type="search"
                      value={toolQuery}
                      onChange={event => setToolQuery(event.target.value)}
                      className={detailsStyles.aiSettingsSearch}
                      placeholder={t('aiToolsSearch', { defaultValue: 'Search tools' })}
                      aria-label={t('aiToolsSearch', { defaultValue: 'Search tools' })}
                    />
                    <span className={detailsStyles.aiSettingsCount}>
                      {t('aiToolsEnabledCount', {
                        defaultValue: '{{enabled}} of {{total}} on',
                        enabled: enabledCount,
                        total: tools.length,
                      })}
                    </span>
                    {canManage ? (
                      <div className={detailsStyles.aiSettingsToolActions}>
                        <button type="button" className={detailsStyles.aiSettingsTextBtn} disabled={saving} onClick={() => setAllFiltered(true)}>
                          {t('aiToolsSelectAll', { defaultValue: 'All' })}
                        </button>
                        <button type="button" className={detailsStyles.aiSettingsTextBtn} disabled={saving} onClick={() => setAllFiltered(false)}>
                          {t('aiToolsSelectNone', { defaultValue: 'None' })}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {filteredTools.length === 0 ? (
                    <div className={detailsStyles.aiSettingsEmpty}>
                      {t('aiToolsNoMatch', { defaultValue: 'No tools match that search.' })}
                    </div>
                  ) : (
                    <div className={detailsStyles.aiSettingsChips}>
                      {filteredTools.map(tool => (
                        <button
                          key={tool.functionId}
                          type="button"
                          className={`${detailsStyles.aiSettingsChip} ${tool.enabled ? detailsStyles.aiSettingsChipOn : ''}`}
                          aria-pressed={tool.enabled}
                          disabled={!canManage || saving}
                          onClick={() => toggleTool(tool.functionId)}
                          title={tool.displayName}
                        >
                          <span className={detailsStyles.aiSettingsChipDot} aria-hidden="true" />
                          <span className={detailsStyles.aiSettingsChipLabel}>{tool.displayName}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {canManage ? (
            <footer className={detailsStyles.aiSettingsFooter}>
              <span className={detailsStyles.aiSettingsFooterHint}>
                {dirty
                  ? t('aiSettingsUnsaved', { defaultValue: 'Unsaved changes' })
                  : t('aiSettingsSavedHint', { defaultValue: 'Saved for this conversation only' })}
              </span>
              <button
                type="button"
                className={detailsStyles.aiSettingsSave}
                onClick={() => void save()}
                disabled={saving || !dirty}
              >
                {saving
                  ? t('saving', { ns: 'common', defaultValue: 'Saving…' })
                  : t('saveAiAssistantSettings', { defaultValue: 'Save' })}
              </button>
            </footer>
          ) : null}
        </>
      )}
    </section>
  );
};

const serializedIds = (ids: string[]) => [...ids].sort((a, b) => a.localeCompare(b)).join('|');

const renderSparkleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path d="M12 3.5 13.4 8.6 18.5 10 13.4 11.4 12 16.5 10.6 11.4 5.5 10 10.6 8.6 12 3.5Z" fill="currentColor" />
    <path d="M18.2 14.5 19 16.8 21.3 17.6 19 18.4 18.2 20.7 17.4 18.4 15.1 17.6 17.4 16.8 18.2 14.5Z" fill="currentColor" opacity="0.85" />
  </svg>
);

const renderCloseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

export default ConversationAiSettingsPanel;
